/**
 * Shared copy/export utilities for clipboard and image conversion.
 * Ported from mojoDoc's copy-helpers.ts for reuse by content overlays.
 */

/**
 * Clean SVG for export: replace <foreignObject> with <text> for canvas compatibility.
 * Mermaid uses foreignObject for labels which canvas can't render directly.
 */
export function cleanSvgForExport(svgElement: SVGElement): SVGElement {
  const clone = svgElement.cloneNode(true) as SVGElement;
  const foreignObjects = clone.querySelectorAll("foreignObject");
  foreignObjects.forEach((fo) => {
    const text = fo.textContent?.trim() || "";
    const svgText = document.createElementNS("http://www.w3.org/2000/svg", "text");
    svgText.setAttribute("x", fo.getAttribute("x") || "0");
    svgText.setAttribute("y", fo.getAttribute("y") || "0");
    svgText.setAttribute("font-size", "14");
    svgText.setAttribute("fill", "currentColor");
    svgText.textContent = text;
    fo.replaceWith(svgText);
  });
  return clone;
}

/**
 * Convert an SVG element to a PNG blob at the given scale (default 6×).
 */
export async function svgToPngBlob(svgElement: SVGElement, scale: number = 6): Promise<Blob> {
  const cleaned = cleanSvgForExport(svgElement);
  const svgString = new XMLSerializer().serializeToString(cleaned);
  const bbox = svgElement.getBoundingClientRect();
  const width = Math.max(bbox.width, 100);
  const height = Math.max(bbox.height, 100);

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get 2D canvas context");
  ctx.scale(scale, scale);

  // Fill white background for transparent SVGs
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (pngBlob) => {
          if (pngBlob) resolve(pngBlob);
          else reject(new Error("Failed to create PNG"));
        },
        "image/png",
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load SVG as image"));
    };
    img.src = url;
  });
}

/**
 * Copy a PNG blob to the clipboard via the Clipboard API.
 */
export async function copyImageToClipboard(blob: Blob): Promise<void> {
  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
}

/**
 * Extract tab-separated values from a table DOM element.
 */
export function tableToTsv(tableElement: HTMLTableElement): string {
  const rows = tableElement.querySelectorAll("tr");
  return Array.from(rows)
    .map((row) => {
      const cells = row.querySelectorAll("th, td");
      return Array.from(cells)
        .map((cell) => (cell.textContent ?? "").trim().replace(/\t/g, " "))
        .join("\t");
    })
    .join("\n");
}

/**
 * Copy plain text to the clipboard.
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

/**
 * Fetch an image and return as PNG blob (for clipboard copy).
 * If the image is already PNG, returns it directly.
 */
export async function imageToPngBlob(src: string): Promise<Blob> {
  const response = await fetch(src, { mode: 'cors' });
  if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
  const imageBlob = await response.blob();

  // If already PNG, return directly
  if (imageBlob.type === "image/png") return imageBlob;

  // Convert via canvas
  const bitmap = await createImageBitmap(imageBlob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get 2D canvas context");
  ctx.drawImage(bitmap, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas to blob failed"));
      },
      "image/png",
    );
  });
}
